import { queryOptions } from "@tanstack/react-query";
import { apiClient } from "~/lib/client";
import { handleApiError, handleResponse } from "~/utils/response";
import { GetRefillsByIdResponse } from "../../pages/quick-actions/api/types";
import { refillKeys } from "./keys";



interface GetRefillsByIdOptions {
    id: string;
}


export const getRefillsByIdQueryOptions = (options: GetRefillsByIdOptions) => {
    return queryOptions({
        queryKey: refillKeys.refillsById(options.id),
        retry: false,
        queryFn: async () => {
            try {
                const response = await apiClient(`/admin/refills/${options.id}`);
                const result = await handleResponse<GetRefillsByIdResponse>(response);

                if (!result.success) {
                    throw new Error(result.error);
                }

                return result.data;
            } catch (error) {
                handleApiError(error);
                throw error;
            }
        },
        select(data) {

            // Check if data has the expected structure
            if (!data || !data.refill) {
                console.error("Invalid data structure in refills query:", data);
                return null;
            }

            try {
                // Check if this is a batch refill
                const isBatch = data.is_batch || (data.refill.refills && Array.isArray(data.refill.refills));

                if (isBatch && data.refill.refills) {
                    // Handle batch refills - return array of medications with individual URLs
                    const transformedData = {
                        refillId: data.refill.refill_batch_id || data.refill.refill_id,
                        refillBatchId: data.refill.refill_batch_id,
                        refillCount: data.refill.refill_count,
                        isBatch: true,
                        patientName: data.refill.patient?.name,
                        patientEmail: data.refill.patient?.email,
                        patientId: data.refill.patient?.user_id ?? "",
                        prescriptionId: data.refill.prescription_id,
                        userDosespotId: data.refill.patient?.user_dosespot_id ?? "",
                        programEnrollment: "Weight Management Plus",
                        // Don't combine URLs - each medication will have its own
                        imageUrls: [],
                        fileUrls: [],
                        medications: data.refill.refills.map(refill => ({
                            name: refill.medication?.name,
                            dosage: refill.medication?.dosage,
                            quantity: refill.medication?.quantity,
                            duration: refill.medication?.duration,
                            refills: refill.medication?.refills,
                            notes: refill.medication?.notes,
                            cost: refill.cost,
                            refillId: refill.refill_id,
                            deliveryStatus: refill.delivery_status,
                            deliveryDate: refill.delivery_date,
                            paymentStatus: refill.payment_status,
                            status: refill.refill_status,
                            type: refill.type,
                            // Each medication gets its own URLs
                            imageUrls: refill.urls?.images || [],
                            fileUrls: refill.urls?.files || []
                        })),
                        requestedAt: data.refill.refills[0]?.request_datetime || data.refill.request_date,
                        paymentStatus: data.refill.refills[0]?.payment_status,
                        sideEffects: data.refill.side_effects || {},
                        weightHistory: {
                            initalWeight: data.refill.weight_history?.initial?.weight,
                            currentWeight: data.refill.weight_history?.current?.weight,
                        }
                    };
                    return transformedData;
                } else {
                    // Handle single refill - return single medication
                    const transformedData = {
                        refillId: data.refill.refill_id,
                        isBatch: false,
                        patientName: data.refill.patient?.name,
                        patientEmail: data.refill.patient?.email,
                        patientId: data.refill.patient?.user_id ?? "",
                        prescriptionId: data.refill.prescription_id,
                        userDosespotId: data.refill.patient?.user_dosespot_id ?? "",
                        programEnrollment: "Weight Management Plus",
                        imageUrls: data.refill.urls?.images || [],
                        fileUrls: data.refill.urls?.files || [],
                        medications: {
                            name: data.refill.medication?.name,
                            dosage: data.refill.medication?.dosage,
                            quantity: data.refill.medication?.quantity,
                            duration: data.refill.medication?.duration,
                            vitamin: data.refill.medication?.vitamin?.name,
                            refills: data.refill.medication?.refills
                        },
                        weightHistory: {
                            initalWeight: data.refill.weight_history?.initial?.weight,
                            currentWeight: data.refill.weight_history?.current?.weight,
                        },
                        requestedAt: data.refill.request_date,
                        paymentStatus: data.refill.payment_status,
                        sideEffects: data.refill.side_effects
                    };
                    return transformedData;
                }
            } catch (error) {
                console.error("Error in refills query select function:", error);
                console.error("Data that caused error:", data);
                return null;
            }
        }
    })
}
